/**
 * TeamLogStream — renders the legacy team_log_messages feed inside the new
 * three-panel shell. Ported from the pre-scaffold viewer (commit b1686a3),
 * adapted to the new light-mode token set.
 *
 * Per planner ship-condition WRr76x41kBr5isc74g7y, the five tag groups
 * (blocker / needs-review / build-complete / approved / reference) must all
 * resolve to a non-transparent border color via the --tag-* vars in
 * src/index.css. Order matters in TAG_GROUPS — first match wins.
 *
 * Real component work (ERThreadCard2, ERThreadEntry2 with Firestore
 * onSnapshot on the nested relay_messages path) replaces this in a later PR.
 */

import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import ReactMarkdown from "react-markdown";
import { linkifyHowRelayWorks, howRelayWorksSlugFromHref } from "../lib/howRelayWorksLinks";
import { HowRelayWorksLink } from "./HowRelayWorksText";
import type { TeamLogMessage } from "../types";
import { tagPillStyle } from "../lib/tagColors";
import { type ViewMode } from "../lib/chatFilter";
import { BuildCompleteCard } from "./BuildCompleteCard";
import { CollapsibleCard } from "./CollapsibleCard";
import { CollapsibleEntry } from "./CollapsibleEntry";
import { AgentSpokeAvatar, roleFromAuthor } from "./Avatars";
import { generateSummary } from "../lib/summaryGenerator";
import { authorLabelFor } from "../lib/authorLabel";
import { isSystemPost, readShowSystemPosts, writeShowSystemPosts } from "../lib/systemPosts";

interface Props {
  messages: TeamLogMessage[];
  /**
   * Active tag filters (AND logic). Sprint `IfXyyJg01e1ox2VAIZIU`, Commit A.
   * When empty, all messages render. When populated, a message shows iff
   * every filter tag is present in `msg.tags`. Messages with null/empty
   * tags are excluded when any filter is active.
   */
  activeFilters?: string[];
  /**
   * relay-S3-sdlcfilters — OR-mode tag set layered on top of the AND
   * filter. Used by per-project SDLC sub-rows (Builds = `build-complete`
   * OR `deployed`, Findings = `finding` OR `defect`, etc.). When set
   * non-empty, a message must include AT LEAST ONE of these tags in
   * addition to passing the AND filter. Null/empty disables the layer
   * (current AND-only behavior).
   */
  sdlcTagSet?: string[] | null;
  tagQuery?: string;
  /**
   * Click handler on inline message tag pills — adds (or removes) that
   * tag from the active filter set in the parent. Filter state itself
   * lives in App.tsx so the Action Required panel's tag search (Commit
   * B) can share the same set.
   */
  onToggleFilter?: (tag: string) => void;
  /**
   * Commit B — when set, the message card with this id renders a purple
   * outline for 1.2s, cleared via setTimeout. Lookup is by
   * data-message-id on the card element.
   */
  flashedId?: string | null;
  /**
   * Sprint 2 Commit 3 (`FGrrZUydSmB9HyRJ0KkA`) — pagination wiring. When
   * `hasMoreEarlier` is true and `onLoadEarlier` is provided, the stream
   * renders a "Load earlier messages" button at the top. Click fires
   * `onLoadEarlier`; parent handles the Firestore getDocs + state merge.
   */
  hasMoreEarlier?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  /**
   * track:log-jump — full-history paging for the active tag filter (served by
   * GET /api/messages/by-tag). Renders a "Load more" button under the filter
   * count and the failure text when a fetch failed.
   */
  tagHistory?: {
    hasMore: boolean;
    loading: boolean;
    error: string | null;
    onLoadMore: () => void;
  };
  /**
   * Sprint 3 Commit 2 (`c6jzMwNsTgfP0W2hi3Wq`) — current user's Firebase
   * Auth UID. When a message has acknowledged_by.includes(myUid), the
   * card renders a muted `✓` next to the timestamp so the user sees
   * at-a-glance which items they've already acted on without opening
   * the Action Required panel.
   */
  myUid?: string;
  /**
   * relay-47 — click handler for the inline `Re: #NNN` chip on threaded
   * replies. App.tsx finds the message with `sequence === seq`, scrolls
   * its `[data-message-id]` into view, and flashes via `flashedId`.
   */
  onScrollToSequence?: (seq: number) => void;
  /**
   * relay-47 — click handler for the per-row sequence chip. Fires
   * `relay:reply-to-message` with the clicked message's sequence so the
   * composer threads the next post as a reply.
   */
  onStartReply?: (seq: number) => void;
  viewMode?: ViewMode;
  /**
   * relay-55 Phase 2 — current user's author string (lowercase email
   * local-part, matching `ERComposerV1.deriveAuthor`). Drives bubble
   * alignment in chat mode. Optional in log mode.
   */
  selfAuthor?: string;
  /**
   * ui/left-nav-project-tag-fix — names of every project in this workspace
   * (e.g. `["relay", "juju"]`). When an `activeFilters` entry matches one of
   * these names exactly, it's treated as a project scope and OR-matched
   * against both the legacy bare tag (`relay`) and the current
   * `project:`-prefixed convention (`project:relay`) — see Charter's
   * "Project Tagging" section (effective Sprint 80). Every other filter
   * entry (blocker, pre-flight, custom sub-filters, etc.) keeps exact
   * single-tag AND matching, unchanged. Optional — omitting it falls back
   * to the old bare-tag-only behavior.
   */
  projectNames?: readonly string[];
  /**
   * track-backlog--virtualize-activity-log — the scrollable ancestor element
   * (e.g. App.tsx's `streamRef` section) that the windowed renderer measures
   * scroll offset/viewport against. Required to enable virtualization; when
   * omitted, all rows render eagerly (unvirtualized fallback).
   */
  scrollParentRef?: RefObject<HTMLElement | null>;
  /**
   * LOG-HUMAN-VIEW — true while the #seq jump view is showing. The jump view
   * always shows the post and its neighbours, so system-post hiding is off.
   */
  jumpActive?: boolean;
}

/**
 * Imperative handle exposed via ref — lets App.tsx scroll a specific message
 * into view even when it's outside the currently-rendered virtual window
 * (a plain `querySelector('[data-message-id]')` only finds mounted rows).
 */
export interface TeamLogStreamHandle {
  /** Returns true if the message was found (and a scroll was initiated). */
  scrollToMessageId: (id: string) => boolean;
}

type Row =
  | { kind: "day"; key: string; day: string }
  | { kind: "message"; key: string; msg: TeamLogMessage };

const DAY_MARKER_ESTIMATE = 32;
const MESSAGE_ROW_ESTIMATE = 96;

const TAG_GROUPS: { match: string[]; color: string; bg: string }[] = [
  { match: ["blocker"], color: "var(--tag-blocker)", bg: "var(--tag-blocker-bg)" },
  { match: ["directive"], color: "#9C27B0", bg: "transparent" }, // Track 20-phase2: Purple directive category
  { match: ["needs-review", "pre-flight", "for-review"], color: "var(--tag-review)", bg: "var(--tag-review-bg)" },
  { match: ["build-complete", "ready-to-stage", "bundle-complete", "pushed"], color: "var(--tag-ready)", bg: "var(--tag-ready-bg)" },
  { match: ["reference"], color: "var(--tag-reference)", bg: "var(--tag-reference-bg)" },
  { match: ["approved", "ritual-complete"], color: "var(--tag-approved)", bg: "var(--tag-approved-bg)" },
];

function tagGroupFor(tags: string[] | undefined): { color: string; bg: string } | null {
  if (!tags || tags.length === 0) return null;
  for (const group of TAG_GROUPS) {
    if (tags.some(t => group.match.includes(t))) {
      return { color: group.color, bg: group.bg };
    }
  }
  return null;
}

function shortTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

// #1359 Assignment 2 — render the calendar day a message belongs to,
// used as the day-marker label (e.g. "Tuesday, April 27, 2026"). Output
// is also the equality key for boundary detection between iterations.
function formatDay(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Track: universal-collapsed-card-view — Targeted collapse for Team Log messages.
 * Returns true if a message should render collapsed by default.
 *
 * Never collapsed: directives, blockers, approvals, chat, high-priority action messages.
 * Always collapsed: technical specs, investigations, long-form content.
 */
function shouldCollapse(msg: TeamLogMessage): boolean {
  const tags = msg.tags || [];

  const neverCollapse = [
    "directive", "blocker", "approved", "rejected", "commit-approved",
    "from-modal", "chat", "acknowledged", "peer-review",
    "session-start", "heartbeat", "charter-check",
  ];
  if (tags.some(t => neverCollapse.includes(t))) return false;

  const alwaysCollapse = [
    "build-complete", "delegation-plan", "state-capsule",
    "finding", "pre-flight", "pre-flight-draft",
  ];
  if (tags.some(t => alwaysCollapse.includes(t))) return true;

  const lines = msg.content.split("\n");
  if (lines.length > 20) return true;
  if (lines.some(line => /^##\s+/.test(line))) return true;

  if (lines.filter(l => l.trim()).length < 5) return false;

  return false;
}


/**
 * Link renderer for Log messages. A How Relay Works "/#slug" link opens that
 * panel in-app (see HowRelayWorksLink);
 * every other link opens in a new tab.
 */
function MessageLink({ children, href, ...rest }: React.ComponentPropsWithoutRef<"a"> & { node?: unknown }) {
  const { node: _node, ...anchorProps } = rest;
  const slug = howRelayWorksSlugFromHref(href);
  if (slug) return <HowRelayWorksLink slug={slug} {...anchorProps}>{children}</HowRelayWorksLink>;
  return (
    <a {...anchorProps} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--er-accent)" }}>
      {children}
    </a>
  );
}

/**
 * Track 15: Custom ReactMarkdown text component that wraps @mentions
 * with amber highlight spans. Processes inline text nodes and splits
 * on @mention patterns, wrapping each match in a styled span.
 */
function MentionText({ children }: { children?: React.ReactNode }) {
  if (typeof children !== "string") return <>{children}</>;

  const mentionRegex = /@([\w-]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(children)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push(children.substring(lastIndex, match.index));
    }
    // Add the highlighted mention
    parts.push(
      <span key={match.index} className="er-mention-highlight">
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last mention
  if (lastIndex < children.length) {
    parts.push(children.substring(lastIndex));
  }

  return <>{parts.length > 0 ? parts : children}</>;
}

// Client-side hide list. Firestore rules deny delete, and claude-code's
// service account is read-only, so contaminating messages can't be removed
// from the backing collection without a separate admin script. Per planner
// `qt7b2VNwbh4DJJ1L2hTJ` these two ids are planner's paste of the Commit C
// landing sources — they belong in docs/reference, not in the stream.
// Once a server-side admin delete path exists, this constant goes away.
const HIDDEN_MESSAGE_IDS = new Set<string>([
  "n2ZNv1r2DKnXplFDWnP9",
  "qypITi8tLlamFyCxxPkK",
]);

export const TeamLogStream = forwardRef<TeamLogStreamHandle, Props>(function TeamLogStream({
  messages,
  activeFilters,
  sdlcTagSet,
  tagQuery,
  onToggleFilter,
  flashedId,
  hasMoreEarlier,
  loadingEarlier,
  onLoadEarlier,
  tagHistory,
  myUid,
  onScrollToSequence,
  onStartReply,
  viewMode = "log",
  selfAuthor,
  projectNames,
  scrollParentRef,
  jumpActive,
}, ref) {
  // LOG-HUMAN-VIEW — bookkeeping posts are hidden by default; the choice is
  // remembered per browser and falls back to hidden if storage throws.
  const [showSystem, setShowSystem] = useState<boolean>(() => readShowSystemPosts());
  function toggleShowSystem() {
    const next = !showSystem;
    setShowSystem(next);
    writeShowSystemPosts(next);
  }
  // relay-55 Phase 2 — bubble alignment helper. Returns "self" for the
  // current user's posts (right-aligned in chat mode), "other" for
  // everyone else. In log mode, bubble wrapping is skipped entirely.
  const bubbleSide = (author: string): "self" | "other" =>
    selfAuthor && author === selfAuthor ? "self" : "other";
  // Sprint `IfXyyJg01e1ox2VAIZIU`, Commit A — AND-logic filter. Messages
  // with no tags drop out once any filter is active; otherwise passing the
  // filter requires every active filter tag to be present on the message.
  // Hide list (paste contamination) is applied before filter/sort so those
  // messages are invisible everywhere, not just when filters are active.
  // relay-S4 Task A (seq #2078): Chat view bypasses activeFilters entirely.
  const filters = viewMode === "chat" ? [] : (activeFilters ?? []);
  // ui/left-nav-project-tag-fix — O(1) lookup for the project-name special
  // case below.
  const projectNameSet = projectNames && projectNames.length > 0 ? new Set(projectNames) : null;
  // relay-S3-sdlcfilters — OR-mode SDLC tag set layered on the AND filter.
  // Treated as a no-op when null/empty so the existing path is unchanged.
  const sdlcOr = sdlcTagSet && sdlcTagSet.length > 0 ? sdlcTagSet : null;
  // Real-time tag search: case-insensitive substring match on any tag.
  // Chat view bypasses this the same way it bypasses activeFilters.
  const tagQ = viewMode === "chat" ? "" : (tagQuery?.trim().toLowerCase() ?? "");
  const baseVisible = messages.filter(m => !HIDDEN_MESSAGE_IDS.has(m.id));
  const visible = baseVisible;
  const hasFilter = filters.length > 0 || !!sdlcOr || !!tagQ;
  const filteredAll = !hasFilter
    ? visible
    : visible.filter(m => {
        if (!m.tags || m.tags.length === 0) return false;
        if (tagQ && !m.tags.some(t => t.toLowerCase().includes(tagQ))) return false;
        if (filters.length > 0 && !filters.every(f => {
          // ui/left-nav-project-tag-fix — a project-name filter entry
          // matches either tag form a message might carry: the legacy bare
          // name or the current `project:`-prefixed convention. Every
          // non-project filter (blocker, pre-flight, custom sub-filters...)
          // keeps the original exact single-tag match.
          if (projectNameSet?.has(f)) {
            return m.tags!.includes(f) || m.tags!.includes(`project:${f}`);
          }
          return m.tags!.includes(f);
        })) return false;
        if (sdlcOr && !sdlcOr.some(t => m.tags!.includes(t))) return false;
        return true;
      });
  // LOG-HUMAN-VIEW — a filter that names a system tag is an explicit ask for
  // those posts, so it is not hidden behind the switch.
  const hidingSystem = !showSystem && !jumpActive && !filters.some(f => isSystemPost({ author: "", tags: [f] }));
  const hiddenSystemCount = hidingSystem ? filteredAll.filter(isSystemPost).length : 0;
  const filtered = hiddenSystemCount > 0 ? filteredAll.filter(m => !isSystemPost(m)) : filteredAll;
  // track-backlog--virtualize-activity-log — flatten filtered messages into
  // a row list (day markers + messages) once per `filtered` change, instead
  // of computing day boundaries inline during render.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    let lastDay: string | null = null;
    for (const msg of filtered) {
      const day = formatDay(msg.timestamp);
      if (day !== lastDay) {
        out.push({ kind: "day", key: `day-${day}`, day });
        lastDay = day;
      }
      out.push({ kind: "message", key: msg.id, msg });
    }
    return out;
  }, [filtered]);

  // Virtualization is only active when a scroll parent is supplied (the
  // Activity Log's `streamRef` section in App.tsx). `enabled: false` skips
  // the resize observers entirely when absent, and the fallback render path
  // below renders every row eagerly — unchanged behavior for that case.
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollParentRef?.current ?? null,
    estimateSize: (index) => (rows[index]?.kind === "day" ? DAY_MARKER_ESTIMATE : MESSAGE_ROW_ESTIMATE),
    overscan: 8,
    getItemKey: (index) => rows[index]?.key ?? index,
    enabled: !!scrollParentRef,
  });

  // When `scrollParentRef` points at an ancestor host node that mounts in
  // the same commit as this component (e.g. switching into the Activity Log
  // view for the first time), React's bottom-up layout-effect order means
  // the virtualizer's own layout effect can run and measure a still-null
  // `scrollParentRef.current` before the ancestor's ref is attached. A
  // regular `useEffect` always fires after every ref in the commit is
  // attached, so forcing one extra render after mount guarantees the
  // virtualizer's next measurement pass sees the real element.
  const [, forceRemeasure] = useState(0);
  useEffect(() => {
    if (scrollParentRef) forceRemeasure(n => n + 1);
  }, [scrollParentRef]);

  // Exposes scroll-to-message for App.tsx's `Re: #NNN` / density-rail / search
  // navigation, which previously relied on `querySelector('[data-message-id]')`
  // — that only finds rows currently mounted in the DOM, which virtualization
  // no longer guarantees.
  useImperativeHandle(ref, () => ({
    scrollToMessageId: (id: string) => {
      const index = rows.findIndex(r => r.kind === "message" && r.msg.id === id);
      if (index === -1) return false;
      if (scrollParentRef) {
        virtualizer.scrollToIndex(index, { align: "center", behavior: "smooth" });
      }
      return true;
    },
  }), [rows, virtualizer, scrollParentRef]);

  function renderMessage(msg: TeamLogMessage): ReactNode {
    const authorLabel = authorLabelFor(msg.author);
    const authorRole = roleFromAuthor(msg.author);
    const tagGroup = tagGroupFor(msg.tags);
    const borderColor = tagGroup?.color ?? "transparent";
    const isFlashed = flashedId === msg.id;
    const acked = !!(myUid && msg.acknowledged_by?.includes(myUid));

    // Track 15 — Visual differentiation system
    const isRelayPost = msg.author === "relay";
    const containsMention = /@[\w-]+/.test(msg.content);

    // Priority: @relay > @mention > tagGroup
    let visualBorder = borderColor;
    let visualBackground = "transparent";

    if (isRelayPost) {
      // Layer 1: @relay posts get teal border + subtle background tint
      visualBorder = "var(--accent)";
      visualBackground = "rgba(79, 142, 247, 0.03)";
    } else if (containsMention) {
      // Layer 2: @mention posts get amber border
      visualBorder = "#F0C430";
    }

    const visualBorderStyle = isRelayPost || containsMention
      ? `3px solid ${visualBorder}`
      : `2px solid ${visualBorder}`;
    // relay-55 Phase 2 — chat-mode bubble alignment.
    const isChatMode = viewMode === "chat";
    const side = bubbleSide(msg.author);
    // Sprint 5 C3 (EFISSUE-812) — render build-complete messages as
    // structured cards. Wrapper keeps `data-message-id` so the
    // scroll-to + flash code in App.tsx still works on cards.
    if (msg.tags?.includes("build-complete")) {
      const card = (
        <div
          data-message-id={msg.id}
          style={{
            outline: isFlashed ? "2px solid var(--er-accent)" : "none",
            outlineOffset: isFlashed ? 2 : 0,
            transition: "outline-color 200ms ease-out",
            borderRadius: 8,
          }}
        >
          <BuildCompleteCard message={msg} />
        </div>
      );
      return isChatMode ? (
        <div className={`er-chat-row er-chat-row-${side}`}>
          {card}
        </div>
      ) : card;
    }
    // Track D — CollapsibleEntry for all log-mode entries (chat mode unchanged).
    if (!isChatMode) {
      return <CollapsibleEntry
          message={msg}
          flashId={flashedId}
          activeFilters={filters}
          onToggleFilter={onToggleFilter}
          onStartReply={onStartReply}
          onScrollToSequence={onScrollToSequence}
        />;
    }
    // Track: universal-collapsed-card-view — render targeted-collapse messages
    // as collapsible cards with generated summaries
    if (shouldCollapse(msg) && !msg.tags?.includes("build-complete")) {
      const summary = generateSummary(msg);
      const card = (
        <div
          data-message-id={msg.id}
          style={{
            outline: isFlashed ? "2px solid var(--er-accent)" : "none",
            outlineOffset: isFlashed ? 2 : 0,
            transition: "outline-color 200ms ease-out",
            borderRadius: 8,
          }}
        >
          <CollapsibleCard
            message={msg}
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <AgentSpokeAvatar role={authorRole} size={22} />
                <span style={{ fontSize: 13, color: "var(--text2)", flex: 1, minWidth: 0 }}>
                  {summary}
                </span>
                {msg.sequence != null && (
                  <span style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-mono)", flexShrink: 0 }}>
                    #{msg.sequence}
                  </span>
                )}
              </div>
            }
          >
            <div
              style={{
                padding: "10px 14px",
                borderTop: "1px solid var(--border)",
                fontSize: 12,
                color: "var(--text)",
                lineHeight: 1.55,
              }}
            >
              <ReactMarkdown
                components={{
                  code: ({ children, ...rest }) => (
                    <code
                      {...rest}
                      style={{
                        background: "var(--er-surface-2)",
                        padding: "1px 4px",
                        borderRadius: 3,
                        fontFamily: "var(--er-font-mono)",
                        fontSize: 12,
                      }}
                    >
                      {children}
                    </code>
                  ),
                  a: MessageLink,
                  text: MentionText,
                }}
              >
                {linkifyHowRelayWorks(msg.content)}
              </ReactMarkdown>
            </div>
          </CollapsibleCard>
        </div>
      );
      return isChatMode ? (
        <div className={`er-chat-row er-chat-row-${side}`}>
          {card}
        </div>
      ) : card;
    }
    const row = (
      <div
        className="er-activity-row"
        data-message-id={msg.id}
        style={{
          borderLeft: visualBorderStyle,
          background: visualBackground,
          outline: isFlashed ? "2px solid var(--er-accent)" : "none",
          outlineOffset: isFlashed ? 2 : 0,
          transition: "outline-color 200ms ease-out",
        }}
      >
        <div className="er-activity-time">{shortTime(msg.timestamp)}</div>
        <div title={authorLabel}>
          <AgentSpokeAvatar role={authorRole} size={22} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {isChatMode && onStartReply && msg.sequence != null ? (
              <button
                type="button"
                className="er-activity-author"
                onClick={() => onStartReply(msg.sequence!)}
                title={`Reply to ${authorLabel}`}
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  font: "inherit",
                  color: "inherit",
                }}
              >
                {authorLabel}
              </button>
            ) : (
              <span className="er-activity-author">{authorLabel}</span>
            )}
            {msg.tags && msg.tags.length > 0 && msg.tags.map(t => {
              const style = tagPillStyle(t);
              const clickable = !!onToggleFilter;
              const isActive = filters.includes(t);
              const baseStyle: CSSProperties = {
                background: style.bg,
                color: style.color,
                border: style.border,
                margin: 0,
                lineHeight: 1.3,
                outline: isActive ? `2px solid ${style.color}` : "none",
                outlineOffset: isActive ? 1 : 0,
              };
              if (!clickable) {
                return (
                  <span key={t} className="er-project-chip" style={baseStyle}>
                    {t}
                  </span>
                );
              }
              return (
                <button
                  key={t}
                  type="button"
                  className="er-project-chip"
                  onClick={() => onToggleFilter!(t)}
                  title={isActive ? `Remove filter: ${t}` : `Filter stream by: ${t}`}
                  style={{ ...baseStyle, cursor: "pointer" }}
                >
                  {t}
                </button>
              );
            })}
            {msg.linkedIssue && (
              <span
                className="er-activity-kind"
                style={{ color: "var(--er-accent)" }}
              >
                {msg.linkedIssue}
              </span>
            )}
            {msg.tags?.includes("from-modal") && (
              <span
                title="Posted from modal"
                style={{
                  fontSize: 11,
                  color: "var(--er-accent)",
                  background: "var(--surface2)",
                  padding: "1px 6px",
                  borderRadius: 4,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontFamily: "var(--er-font-mono)",
                }}
              >
                ✓ Modal
              </span>
            )}
          </div>
          {msg.replyToSequence != null && (
            <button
              type="button"
              onClick={() => onScrollToSequence?.(msg.replyToSequence!)}
              title={`Scroll to message #${msg.replyToSequence}`}
              style={{
                alignSelf: "flex-start",
                marginTop: 4,
                padding: "1px 6px",
                fontSize: 10,
                fontFamily: "var(--er-font-mono)",
                color: "var(--er-accent)",
                background: "rgba(79, 142, 247, 0.08)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                cursor: onScrollToSequence ? "pointer" : "default",
                lineHeight: 1.4,
              }}
            >
              Re: #{msg.replyToSequence}
            </button>
          )}
          <div className="er-activity-text" style={{ wordBreak: "break-word" }}>
            <ReactMarkdown
              components={{
                code: ({ children, ...rest }) => (
                  <code
                    {...rest}
                    style={{
                      background: "var(--er-surface-2)",
                      padding: "1px 4px",
                      borderRadius: 3,
                      fontFamily: "var(--er-font-mono)",
                      fontSize: 12,
                    }}
                  >
                    {children}
                  </code>
                ),
                a: MessageLink,
                text: MentionText,
              }}
            >
              {linkifyHowRelayWorks(msg.content)}
            </ReactMarkdown>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 2,
            paddingTop: 4,
          }}
        >
          {msg.sequence != null && (
            onStartReply ? (
              <button
                type="button"
                onClick={() => onStartReply(msg.sequence!)}
                title={`Reply to message #${msg.sequence}`}
                style={{
                  fontSize: 10,
                  color: "var(--er-fg-4)",
                  fontFamily: "var(--er-font-mono)",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                #{msg.sequence}
              </button>
            ) : (
              <span
                title={`Message #${msg.sequence}`}
                style={{
                  fontSize: 10,
                  color: "var(--er-fg-4)",
                  fontFamily: "var(--er-font-mono)",
                }}
              >
                #{msg.sequence}
              </span>
            )
          )}
          {acked && (
            <span
              title="Acknowledged by you"
              aria-label="Acknowledged by you"
              style={{ fontSize: 11, color: "var(--er-fg-4)", lineHeight: 1 }}
            >
              ✓
            </span>
          )}
        </div>
      </div>
    );
    return isChatMode ? (
      <div className={`er-chat-row er-chat-row-${side}`}>
        <div className={`er-chat-bubble er-chat-bubble-${side}`}>
          {row}
        </div>
      </div>
    ) : row;
  }

  function renderRow(row: Row): ReactNode {
    if (row.kind === "day") {
      return (
        <div
          className="er-stream-day-marker"
          style={{
            textAlign: "center",
            fontSize: 11,
            color: "var(--text3)",
            padding: "8px 0 4px",
            fontFamily: "var(--font-mono)",
            letterSpacing: 0.3,
          }}
        >
          — {row.day} —
        </div>
      );
    }
    return renderMessage(row.msg);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {!jumpActive && (hiddenSystemCount > 0 || showSystem) && (
        <div
          style={{ textAlign: "center", fontSize: 11, color: "var(--text3)", padding: "2px 0" }}
        >
          {showSystem
            ? "Showing system posts · "
            : `${hiddenSystemCount} system post${hiddenSystemCount === 1 ? "" : "s"} hidden · `}
          <button
            type="button"
            onClick={toggleShowSystem}
            style={{
              fontSize: 11,
              padding: 0,
              background: "transparent",
              border: "none",
              color: "var(--er-accent)",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {showSystem ? "Hide" : "Show"}
          </button>
        </div>
      )}
      {onLoadEarlier && hasMoreEarlier && !hasFilter && (
        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <button
            type="button"
            onClick={onLoadEarlier}
            disabled={!!loadingEarlier}
            style={{
              fontSize: 11,
              padding: "4px 12px",
              background: "var(--surface2)",
              color: "var(--text2)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              cursor: loadingEarlier ? "wait" : "pointer",
              fontFamily: "var(--font-mono)",
            }}
          >
            {loadingEarlier ? "Loading…" : "Load earlier messages"}
          </button>
        </div>
      )}
      {hasFilter && (
        <div
          style={{
            textAlign: "center",
            fontSize: 11,
            color: "var(--text3)",
            padding: "2px 0 6px",
          }}
        >
          {filtered.length === 0
            ? tagHistory?.loading
              ? "Searching full history…"
              : tagHistory?.error ?? "No posts match. Try clearing a filter or adjusting the search."
            : filters.length > 0
              ? `${filtered.length} of ${visible.length} messages match current filters`
              : `${filtered.length} of ${visible.length} messages match`}
          {tagHistory && filters.length > 0 && (tagHistory.hasMore || tagHistory.error) && (
            <button
              type="button"
              onClick={tagHistory.onLoadMore}
              disabled={tagHistory.loading}
              style={{
                marginLeft: 10,
                fontSize: 11,
                padding: "2px 8px",
                cursor: tagHistory.loading ? "wait" : "pointer",
                color: "var(--text2)",
                background: "var(--surface2)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                fontFamily: "var(--font-mono)",
              }}
            >
              {tagHistory.loading ? "Loading…" : tagHistory.error ? "Retry" : "Load more"}
            </button>
          )}
        </div>
      )}
      {scrollParentRef ? (
        <div style={{ position: "relative", height: virtualizer.getTotalSize(), width: "100%" }}>
          {virtualizer.getVirtualItems().map(virtualRow => {
            const row = rows[virtualRow.index];
            if (!row) return null;
            return (
              <div
                key={row.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {renderRow(row)}
              </div>
            );
          })}
        </div>
      ) : (
        rows.map(row => <Fragment key={row.key}>{renderRow(row)}</Fragment>)
      )}
    </div>
  );
});
